/**
 * Form for configuring pass/fail scoring criteria for a SIM level.
 *
 * @module components/sim/createLevels/PassFailCriteria
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useEffect, useState, useRef } from 'react';
import { connect } from "react-redux";
import {
    Button,
    Dialog,
    TextField,
    DialogContent,
    Typography,
    DialogTitle,
    Checkbox,
    Tooltip,
    Divider,
    Modal,
} from '@material-ui/core';
import HelpIcon from "@material-ui/icons/Help";
import {
    useStyle,
    getModalStyle,
} from './CreateLevels';
import {
    resetPassFail,
    resetPassFailModal,
    setPassFailCriteria,
} from "../../../redux/sims/actions";
import { removeSimPassFail } from '../../../services/result.service';
import PropTypes from "prop-types";
import UseAlert from '../../common/UseAlert';

/**
 * Represents the functionality and state for managing pass/fail criteria within a specific
 * simulation level.
 *
 * The `PassFailCriteria` function component handles the logic for configuring and managing
 * thresholds and states related to the pass/fail criteria for different levels within a
 * simulation. It handles user inputs for setting criteria, opening/closing modals, alerts, and
 * resetting states.
 *
 * @param {function} handlePassDialog - Callback function to handle the opening or closing of the
 *     pass/fail dialog.
 * @param {boolean} passDialog - Boolean state indicating if the pass/fail dialog is currently
 *     open.
 * @param {Object} passFailCriteriaRes - Object representing the current pass/fail criteria
 *     response for a specific level.
 * @param setPassFailCriteria
 * @param {Object} data - Data structure containing simulation information, including levels and
 *     current state.
 * @param {number} currentIndex - The index of the current level within the simulation data.
 * @param {function} resetPassFail - Function to reset the pass/fail criteria state to an initial
 *     configuration.
 * @param {boolean} openPassFailModal - Boolean state indicating if the pass/fail modal is
 *     currently open.
 * @param {function} resetPassFailModal - Function to reset the pass/fail modal state.
 *
 * @property {boolean} passFailBool - State indicating if skip levels are allowed based on current
 *     pass/fail criteria.
 * @property {Object} passFailCriteriaStateRes - Local state storing the pass/fail criteria
 *     response for the current level.
 * @property {boolean} openModal - State indicating whether a modal dialog is currently open.
 * @property {string} modalTitle - Title content for the modal dialog.
 * @property {string} modalBody - Body content for the modal dialog.
 * @property {boolean} showModalButton - State determining whether a confirmation button should be
 *     displayed in the modal.
 * @property {Object} modalStyle - Configuration defining the style for the modal dialog.
 * @property {React.RefObject} keywordInputRef - Reference to the input field for keyword accuracy
 *     threshold.
 * @property {React.RefObject} typingInputRef - Reference to the input field for typing accuracy
 *     threshold.
 * @property {React.RefObject} dataSelInputRef - Reference to the input field for data selection
 *     accuracy threshold.
 * @property {React.RefObject} overAllInputRef - Reference to the input field for overall accuracy
 *     threshold.
 * @property {React.RefObject} skipLevelRef - Reference to the checkbox for allowing skip levels.
 * @property {boolean} disableKeywordAccuracy - State controlling if keyword accuracy threshold
 *     input is disabled.
 * @property {boolean} disableTypingAccuracy - State controlling if typing accuracy threshold input
 *     is disabled.
 * @property {boolean} disableDataSelectionAccuracy - State controlling if data selection accuracy
 *     threshold input is disabled.
 * @property {boolean} disableOverallAccuracy - State controlling if overall accuracy threshold
 *     input is disabled.
 * @property {boolean} unsetPassFailCriteria - State indicating if the pass/fail criteria will be
 *     unset.
 * @function submitPassFailCriteria - Function to collect user input and dispatch the action to set
 *     the pass/fail criteria.
 * @function anywaySubmitPassFail - Function to override existing criteria and submit new pass/fail
 *     criteria.
 *
 * The component also uses the `useEffect` hook to monitor changes in the `passFailCriteriaRes`,
 *     `data`, and modal visibility states to appropriately update and reset criteria-related state
 *     variables or display alerts.
 */
const PassFailCriteria = ({
    handlePassDialog,
    passDialog,
    passFailCriteriaRes,
    setPassFailCriteria,
    data,
    currentIndex,
    resetPassFail,
    openPassFailModal,
    resetPassFailModal,
}) => {
    const {setAlert} = UseAlert();
    const [passFailBool, setPassFailBool] = React.useState(false);
    const [passFailCriteriaStateRes, setPassFailCriteriaStateRes] = useState({});
    const [openModal, setOpenModal] = React.useState(false);
    const [modalTitle, setModalTitle] = React.useState("");
    const [modalBody, setModalBody] = React.useState("");
    const [showModalButton, setShowModalButton] = React.useState(false);
    const [modalStyle] = React.useState(getModalStyle);
    const classes = useStyle();
    const keywordInputRef = useRef(null);
    const typingInputRef = useRef(null);
    const dataSelInputRef = useRef(null);
    const overAllInputRef = useRef(null);
    const skipLevelRef = useRef(null);
    const [disableKeywordAccuracy, setDisableKeywordAccuracy] = React.useState(false);
    const [disableTypingAccuracy, setDisableTypingAccuracy] = React.useState(false);
    const [disableDataSelectionAccuracy, setDisableDataSelectionAccuracy] = React.useState(false);
    const [disableOverallAccuracy, setDisableOverallAccuracy] = React.useState(false);
    const [unsetPassFailCriteria, setUnsetPassFailCriteria] = React.useState(false);

    useEffect(() => {
        if (
            passFailCriteriaRes?.sim_level_id?.toString() === data.levels[currentIndex]?._id?.toString()
        ) {
            setPassFailCriteriaStateRes(passFailCriteriaRes);
            setPassFailBool(
                passFailCriteriaRes.allow_skip_levels
                    ? passFailCriteriaRes.allow_skip_levels
                    : false
            );
            setDisableKeywordAccuracy(
                passFailCriteriaRes.disable_threshold_keyword_accuracy
                    ? passFailCriteriaRes.disable_threshold_keyword_accuracy
                    : false
            );
            setDisableTypingAccuracy(
                passFailCriteriaRes.disable_threshold_typing_accuracy
                    ? passFailCriteriaRes.disable_threshold_typing_accuracy
                    : false
            );
            setDisableDataSelectionAccuracy(
                passFailCriteriaRes.disable_threshold_data_selection_accuracy
                    ? passFailCriteriaRes.disable_threshold_data_selection_accuracy
                    : false
            );
            setDisableOverallAccuracy(
                passFailCriteriaRes.disable_threshold_overall_accuracy
                    ? passFailCriteriaRes.disable_threshold_overall_accuracy
                    : false
            );
        } else {
            setPassFailCriteriaStateRes({});
            setPassFailBool(false);
            setDisableKeywordAccuracy(false);
            setDisableTypingAccuracy(false);
            setDisableDataSelectionAccuracy(false);
            setDisableOverallAccuracy(false);
        }
    }, [
        passFailCriteriaRes,
        setPassFailCriteriaStateRes,
        setPassFailBool,
        currentIndex,
        data
    ]);

    useEffect(() => {
        if (openPassFailModal) {
            setTimeout(() => {
                resetPassFailModal();
            }, 2000);
            setTimeout(
                () => {
                    setAlert('Criteria is set successfully for this level!', 'success');
                },
                500
            );
        }
    },[openPassFailModal, resetPassFailModal]);

    /**
     * Closes the currently open modal and updates the dialog state.
     *
     * This function performs two actions when invoked:
     * 1. Sets the `openModal` state to `false`, ensuring the modal is closed.
     * 2. Calls the `handlePassDialog` function with the argument `"close"`,
     *    allowing additional logic to execute when the modal is closed.
     */
    const handleModalClose = () => {
        setOpenModal(false);
        handlePassDialog("close");
    };

    /**
     * A function that closes the modal and resets or updates state values related to pass/fail
     * criteria based on the provided conditions.
     *
     * Description:
     * - This function is used to close a modal (`setOpenModal(false)`) and handle the dialog state
     * via
     *   `handlePassDialog` with the "close" action.
     * - It resets multiple state values related to pass/fail criteria, such as accuracy and
     * behavior flags.
     * - If the `sim_level_id` in `passFailCriteriaRes` matches the current level ID from
     * `data.levels[currentIndex]`, it updates the state properties with values extracted from
     * `passFailCriteriaRes`.
     * - If no match is found, it reverts all related states to their default values.
     *
     * Side Effects:
     * - Updates various states such as:
     *   - `setPassFailCriteriaStateRes`: Determines the criteria state response.
     *   - `setPassFailBool`: Indicates whether skipping levels is allowed.
     *   - `setDisableKeywordAccuracy`: Disables keyword accuracy if applicable.
     *   - `setDisableTypingAccuracy`: Disables typing accuracy if applicable.
     *   - `setDisableDataSelectionAccuracy`: Disables data selection accuracy if applicable.
     *   - `setDisableOverallAccuracy`: Disables overall accuracy if applicable.
     *
     * Functionality Flow:
     * 1. The modal is closed, and the pass dialog is updated with a "close" value.
     * 2. The function checks if the `sim_level_id` from `passFailCriteriaRes` matches the current
     * level ID.
     * 3. If matched:
     *    - Relevant states are updated using values from `passFailCriteriaRes`.
     * 4. If not matched:
     *    - Resets relevant states to default values.
     *
     * Preconditions:
     * - Variables such as `passFailCriteriaRes`, `data.levels`, and `currentIndex` must be defined
     * and valid.
     * - State update functions (`setPassFailCriteriaStateRes`, `setOpenModal`, etc.) must be
     * available.
     */
    const closeWithoutChanging = () => {
        setOpenModal(false);
        handlePassDialog("close");
        setUnsetPassFailCriteria(false);
        if (
            passFailCriteriaRes?.sim_level_id?.toString() ===
            data.levels[currentIndex]?._id?.toString()
        ) {
            setPassFailCriteriaStateRes(passFailCriteriaRes);
            setPassFailBool(
                passFailCriteriaRes.allow_skip_levels
                    ? passFailCriteriaRes.allow_skip_levels
                    : false
            );
            setDisableKeywordAccuracy(
                passFailCriteriaRes.disable_threshold_keyword_accuracy
                    ? passFailCriteriaRes.disable_threshold_keyword_accuracy
                    : false
            );
            setDisableTypingAccuracy(
                passFailCriteriaRes.disable_threshold_typing_accuracy
                    ? passFailCriteriaRes.disable_threshold_typing_accuracy
                    : false
            );
            setDisableDataSelectionAccuracy(
                passFailCriteriaRes.disable_threshold_data_selection_accuracy
                    ? passFailCriteriaRes.disable_threshold_data_selection_accuracy
                    : false
            );
            setDisableOverallAccuracy(
                passFailCriteriaRes.disable_threshold_overall_accuracy
                    ? passFailCriteriaRes.disable_threshold_overall_accuracy
                    : false
            );
        } else {
            setPassFailCriteriaStateRes({});
            setPassFailBool(false);
            setDisableKeywordAccuracy(false);
            setDisableTypingAccuracy(false);
            setDisableDataSelectionAccuracy(false);
            setDisableOverallAccuracy(false);
        }
    };

    /**
     * Submits the pass/fail criteria for a simulation or level with specific thresholds and
     * conditions.
     *
     * This function gathers input values for various accuracy thresholds (keyword, typing, data
     * selection, and overall), a flag for allowing skipped levels, and other settings to create
     * the parameters for submitting the criteria. If the pass/fail criteria are being set for the
     * first time, it saves the criteria and closes the related dialog. In cases where the criteria
     * are already set, a confirmation modal is displayed to warn the user before making changes.
     *
     * @function submitPassFailCriteria
     * @description Handles the process of setting or updating pass/fail criteria for a simulation
     *     level. It validates and processes user inputs, creates the criteria parameters, and
     *     updates the state or triggers a modal for confirmation if necessary.
     */
    const submitPassFailCriteria = () => {
        // Create criteria params
        const criteriaParams = {
            threshold_keyword_accuracy:
                keywordInputRef.current.value !== ""
                    ? Number(keywordInputRef.current.value)
                    : undefined,
            threshold_typing_accuracy:
                typingInputRef.current.value !== ""
                    ? Number(typingInputRef.current.value)
                    : undefined,
            threshold_data_selection_accuracy:
                dataSelInputRef.current.value !== ""
                    ? Number(dataSelInputRef.current.value)
                    : undefined,
            threshold_overall_accuracy:
                overAllInputRef.current.value !== ""
                    ? Number(overAllInputRef.current.value)
                    : undefined,
            allow_skip_levels: skipLevelRef.current.checked,
            status: "set",
            sim_level_id: data.levels[currentIndex]._id,
            sim_id: data.id,
            disable_threshold_keyword_accuracy: disableKeywordAccuracy,
            disable_threshold_typing_accuracy: disableTypingAccuracy,
            disable_threshold_data_selection_accuracy: disableDataSelectionAccuracy,
            disable_threshold_overall_accuracy: disableOverallAccuracy,
        };
        // when pass fail set for first time
        if (!data.is_pass_fail_criteria_set) {
            setPassFailCriteria(criteriaParams);
            handlePassDialog("close");

            setTimeout(
                () => {
                    setAlert('Criteria is set successfully for this level!', 'success');
                },
                500
            );
        } else {
            const passFailLevel = data.levels.findIndex(lvl => lvl._id === data.pass_fail_id.sim_level_id);
            setModalTitle("Pass / Fail - Warning");
            setModalBody(
                `Pass / Fail criteria is already set for Level - ${passFailLevel + 1} of this sim, Are you sure you would like to make this change?`
            );
            setShowModalButton(true);
            setOpenModal(true);
        }
    };

    /**
     * anywaySubmitPassFail is a function that processes and sets the pass/fail criteria for
     * simulation levels. It generates a criteria object based on user input values from various
     * form fields and updates the state accordingly. The function also handles closing of modals
     * and dialogs related to the pass/fail criteria setup once the process is complete.
     *
     * Properties included in the criteria object:
     * - threshold_keyword_accuracy: Numeric value derived from a keyword input field.
     * - threshold_typing_accuracy: Numeric value derived from a typing input field.
     * - threshold_data_selection_accuracy: Numeric value derived from a data selection input
     * field.
     * - threshold_overall_accuracy: Numeric value derived from an overall input field.
     * - allow_skip_levels: Boolean value indicating if skipping levels is allowed.
     * - status: Fixed string value "set".
     * - sim_level_id: Identifier for the simulation level being processed.
     * - sim_id: Identifier for the simulation instance.
     * - disable_threshold_keyword_accuracy: Boolean value indicating if the keyword accuracy
     * threshold is disabled.
     * - disable_threshold_typing_accuracy: Boolean value indicating if the typing accuracy
     * threshold is disabled.
     * - disable_threshold_data_selection_accuracy: Boolean value indicating if the data selection
     * accuracy threshold is disabled.
     * - disable_threshold_overall_accuracy: Boolean value indicating if the overall accuracy
     * threshold is disabled.
     * - _id: (Optional) The ID of the criteria object, reused if the current level matches an
     * existing criteria setting.
     *
     * The function performs the following actions:
     * 1. Constructs the `criteriaParams` object based on input references and flags.
     * 2. Reuses an existing criteria `_id` if applicable for the current simulation level.
     * 3. Updates the pass/fail criteria state using `setPassFailCriteriaSuccess`.
     * 4. Closes modal windows and resets modal properties such as title and body content.
     * 5. Invokes `handlePassDialog` to close the pass criteria dialog.
     */
    const anywaySubmitPassFail = () => {
        const criteriaParams = {
            threshold_keyword_accuracy:
                keywordInputRef.current.value !== ""
                    ? Number(keywordInputRef.current.value)
                    : undefined,
            threshold_typing_accuracy:
                typingInputRef.current.value !== ""
                    ? Number(typingInputRef.current.value)
                    : undefined,
            threshold_data_selection_accuracy:
                dataSelInputRef.current.value !== ""
                    ? Number(dataSelInputRef.current.value)
                    : undefined,
            threshold_overall_accuracy:
                overAllInputRef.current.value !== ""
                    ? Number(overAllInputRef.current.value)
                    : undefined,
            allow_skip_levels: skipLevelRef.current.checked,

            status: "set",
            sim_level_id: data.levels[currentIndex]._id,
            sim_id: data.id,
            disable_threshold_keyword_accuracy: disableKeywordAccuracy,
            disable_threshold_typing_accuracy: disableTypingAccuracy,
            disable_threshold_data_selection_accuracy: disableDataSelectionAccuracy,
            disable_threshold_overall_accuracy: disableOverallAccuracy,
        };
        // save _id to criteriaParams, if criteria setting for same level
        if (passFailCriteriaRes._id) {
            criteriaParams._id =
                passFailCriteriaRes?.sim_level_id?.toString() ===
                data.levels[currentIndex]?._id?.toString()
                    ? passFailCriteriaRes._id
                    : undefined;
        }

        setPassFailCriteria(criteriaParams);
        setOpenModal(false);
        setModalTitle("");
        setModalBody("");
        setShowModalButton(false);
        handlePassDialog("close");
    };

    /**
     * Function to unset the Pass/Fail criteria for a simulation.
     * This function sets various modal properties and flags to confirm the action
     * of unsetting the Pass/Fail criteria for a specific simulation.
     */
    const unsetPassFail = () => {
        setModalTitle("Confirm");
        setModalBody(
            `Are you sure want to unset Pass/Fail criteria for this sim`
        );
        setShowModalButton(true);
        setUnsetPassFailCriteria(true)
        setOpenModal(true);
    };

    /**
     * Asynchronous function to remove the "Pass/Fail" criteria for a specific simulation level.
     *
     * Removes the Pass/Fail criteria by invoking the `removeSimPassFail` API with the required
     * identifiers and updates the related state variables accordingly. On successful removal,
     * it resets specific flags, state variables, and closes related UI elements/dialogs.
     *
     * @function
     * @async
     * @returns {Promise<void>} Resolves when the Pass/Fail criteria is successfully removed
     *                          and the UI state is updated.
     */
    const removePassFail = async() => {
        try {
            await removeSimPassFail(data.levels[currentIndex]._id, data.id).then((res) =>{
                if(res.data.success) {
                    setPassFailCriteriaStateRes({});
                    setPassFailBool(false);
                    setDisableKeywordAccuracy(false);
                    setDisableTypingAccuracy(false);
                    setDisableDataSelectionAccuracy(false);
                    setDisableOverallAccuracy(false);
                }
            });
        } catch (error) {
            console.error("Error removing Pass/Fail criteria:", error);
        }

        setUnsetPassFailCriteria(false);
        setOpenModal(false);
        handlePassDialog("close");
        resetPassFail();
        setTimeout(
            () => {
                setAlert('Pass/Fail criteria is unset successfully for this level!', 'success');
            },
            1000
        );
    }

    return (
        <>
            {/*<Button
                variant="outlined"
                className={classes.generateSimBtn}
                onClick={() => handlePassDialog('open')}
            >
                SET PASS/FAIL FOR THE SIM
            </Button>*/}
            <Dialog
                open={passDialog}
                onClose={handlePassDialog}
                aria-labelledby="form-dialog-title"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    <span style={{ margin: '0px' }}>Set Pass Criteria For Level {currentIndex + 1 } </span>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        <strong>
                            Set Pass Criteria (Select NA if criteria not applicable)
                        </strong>
                    </Typography>
                    <div className={classes.passFailDiv}>
                        Threshold Keyword Accuracy
                        {' '}
                        <TextField
                            disabled={disableKeywordAccuracy}
                            type="number"
                            inputRef={keywordInputRef}
                            defaultValue={passFailCriteriaStateRes.threshold_keyword_accuracy}
                            variant="outlined"
                            size="small"
                            className={classes.textfield}
                        />
                        {' '}
                        %
                        <Checkbox
                            checked={disableKeywordAccuracy}
                            onChange={(event) => setDisableKeywordAccuracy(event.target.checked)}
                            name="keyword"
                            className={classes.checkbox}
                        />
                        {' '}
                        NA
                    </div>
                    <div className={classes.passFailDiv}>
                        Threshold Typing Accuracy
                        {' '}
                        <TextField
                            disabled={disableTypingAccuracy}
                            inputRef={typingInputRef}
                            type="number"
                            defaultValue={passFailCriteriaStateRes.threshold_typing_accuracy}
                            variant="outlined"
                            size="small"
                            className={classes.textfield}
                        />
                        {' '}
                        <Checkbox
                            checked={disableTypingAccuracy}
                            onChange={(event) => setDisableTypingAccuracy(event.target.checked)}
                            name="typing"
                            className={classes.checkbox}
                        />
                        {' '}
                        NA
                    </div>
                    <div className={classes.passFailDiv}>
                        Threshold Data Selection Accuracy
                        {' '}
                        <TextField
                            disabled={disableDataSelectionAccuracy}
                            inputRef={dataSelInputRef}
                            defaultValue={passFailCriteriaStateRes.threshold_data_selection_accuracy}
                            type="number"
                            variant="outlined"
                            size="small"
                            className={classes.textfield}
                        />
                        {' '}
                        <Checkbox
                            checked={disableDataSelectionAccuracy}
                            onChange={(event) => setDisableDataSelectionAccuracy(event.target.checked)}
                            name="data"
                            className={classes.checkbox}
                        />
                        {' '}
                        NA
                    </div>
                    <div className={classes.passFailDiv}>
                        Threshold OverAll Accuracy
                        {' '}
                        <TextField
                            disabled={disableOverallAccuracy}
                            inputRef={overAllInputRef}
                            defaultValue={passFailCriteriaStateRes.threshold_overall_accuracy}
                            type="number"
                            variant="outlined"
                            size="small"
                            className={classes.textfield}
                        />
                        {' '}
                        %
                        <Checkbox
                            checked={disableOverallAccuracy}
                            onChange={(event) => setDisableOverallAccuracy(event.target.checked)}
                            name="overall"
                            className={classes.checkbox}
                        />
                        {' '}
                        NA
                    </div>
                    <div className={classes.passFailDiv}>
                        Allow Learner to skip levels
                        {' '}
                        <Checkbox
                            inputRef={skipLevelRef}
                            onChange={() => setPassFailBool(skipLevelRef.current.checked)}
                            checked={passFailBool}
                        />
                        {' '}
                        <Tooltip title="If this box is checked, Learners are allowed to skip levels and go directly to the You Try w/ No Help level and send for review">
                            <HelpIcon style={{ marginBottom: '-8px' }} />
                        </Tooltip>
                    </div>
                    <Divider />
                    <Button
                        onClick={submitPassFailCriteria}
                        color="primary"
                        variant="contained"
                        style={{
                            float: 'right',
                            margin: '0.5rem 1rem 0.5rem 0rem',
                        }}
                    >
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        style={{
                            float: 'right',
                            margin: '0.5rem 1rem 0.5rem 0rem',
                        }}
                        onClick={() => closeWithoutChanging()}
                    >
                        Cancel
                    </Button>
                    {
                        Object.keys(passFailCriteriaStateRes).length > 0 ?
                            <Button
                                variant="contained"
                                style={{
                                    float: 'right',
                                    margin: '0.5rem 1rem 0.5rem 0rem',
                                }}
                                onClick={() => unsetPassFail()}
                            >
                                Unset Pass Fail
                            </Button>
                            : null
                    }

                </DialogContent>

            </Dialog>
            <Modal
                open={openModal}
                onClose={handleModalClose}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
            >
                <div style={modalStyle} className={classes.modalPaper}>
                    <h2>{modalTitle}</h2>
                    <p>{modalBody}</p>
                    {showModalButton ? (
                        <>
                            {unsetPassFailCriteria ?
                                <Button
                                    onClick={removePassFail}
                                    color="primary"
                                    variant="contained"
                                    style={{ float: "right", margin: "0.5rem 1rem 0.5rem 0rem" }}
                                >
                                    Yes
                                </Button> :
                                <Button
                                    onClick={() => anywaySubmitPassFail()}
                                    variant="contained"
                                    style={{ float: "right", margin: "0.5rem 1rem 0.5rem 0rem" }}
                                >
                                    Continue
                                </Button>
                            }
                            <Button
                                onClick={() => closeWithoutChanging()}
                                variant="contained"
                                style={{ float: "right", margin: "0.5rem 1rem 0.5rem 0rem" }}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : null}
                </div>
            </Modal>
        </>
    );
};

PassFailCriteria.propTypes = {
    handlePassDialog: PropTypes.func,
    passDialog: PropTypes.bool,
    passFailCriteriaRes: PropTypes.object,
    setPassFailCriteria: PropTypes.func,
    data: PropTypes.object,
    currentIndex: PropTypes.number,
    resetPassFail: PropTypes.func,
    openPassFailModal: PropTypes.bool,
    resetPassFailModal: PropTypes.func,
}
const mapStateToProps = (state) => ({
    passFailCriteriaRes: state.sim.passFailCriteriaRes,
    openPassFailModal:  state.sim.openPassFailModal,
});

const mapDispatchToProps = {
    resetPassFail,
    resetPassFailModal,
    setPassFailCriteria,
};

export default connect(mapStateToProps, mapDispatchToProps)(PassFailCriteria);
